import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import {
  toInt,
  isStatusVisibleFor,
  isFieldVisibleFor,
  isFieldRequiredAt,
  isFieldEmpty,
  requiresVerification
} from '../utils/fieldRules.js'

/**
 * Calcola, per un progetto della lista, quali campi mancano ancora per poter passare allo stato
 * successivo. Usato dalla colonna "da compilare" della dashboard.
 *
 * Le regole pure stanno in utils/fieldRules.js (incluso l'avviso sulla duplicazione con
 * ProjectForm.vue); qui c'è solo ciò che ha bisogno dello store, delle traduzioni e delle cache.
 */

// ---------- Cache ----------
// Vuetify chiama sortRaw O(n log n) volte: senza memoizzazione un click sull'intestazione
// ricalcolerebbe la lista centinaia di volte. Non è un'ottimizzazione opzionale.

// Requisiti per (tipo processo, stato): non dipendono dai valori della riga, quindi bastano
// poche voci (3 tipi × ~13 stati) per coprire tutta la tabella.
const requirementCache = new Map()

// Risultato per riga. WeakMap sull'oggetto progetto: loadProjects sostituisce projects.value con
// oggetti deserializzati nuovi, quindi un ricaricamento invalida la cache da solo, senza perdite.
const rowCache = new WeakMap()

// `configurations` viene riassegnato da loadConfigurations, quindi la sua identità è un'epoca
// gratuita per cfg.json + fields.json. La lingua entra nell'epoca perché nel risultato ci sono
// etichette tradotte.
let lastConfigRef = null
let configSerial = 0

export function useFieldRequirements() {
  const projectStore = useProjectStore()
  const { t, te, locale } = useI18n()

  const currentEpoch = () => {
    if (projectStore.configurations !== lastConfigRef) {
      lastConfigRef = projectStore.configurations
      configSerial++
      requirementCache.clear()
    }
    return `${configSerial}|${locale.value}`
  }

  // Stati raggiungibili da quello corrente: nextStates, ripiego sequenziale, filtro di visibilità,
  // short-circuit sullo stato esclusivo. Porto di nextStatesOptions (ProjectForm.vue ~:1301).
  const resolveNextStates = (project) => {
    const projectTypeId = toInt(project?.projectType)
    const statusId = toInt(project?.activeStatus)
    if (projectTypeId === null || statusId === null) return []

    const current = projectStore.getStatusInfo(String(statusId), projectTypeId)
    if (!current) return []

    let potential = []

    if (current.nextStates && current.nextStates.length > 0) {
      potential = current.nextStates
        .map(id => projectStore.getStatusInfo(String(id), projectTypeId))
        .filter(s => s != null)
    } else {
      // Parità letterale con l'originale: uno stato terminale (nextStates: []) finisce qui, ma
      // gli stati terminali sono ultimi in statusList, quindi il risultato resta vuoto.
      const list = projectStore.getStatusListForType(projectTypeId)
      const index = list.findIndex(s => s.id === statusId)
      if (index !== -1 && index < list.length - 1) {
        potential = [list[index + 1]]
      }
    }

    const visible = potential.filter(s => isStatusVisibleFor(project, s))

    // Uno stato esclusivo visibile diventa l'unica destinazione possibile
    const exclusive = visible.find(s => s.exclusive)
    return exclusive ? [exclusive] : visible
  }

  // Campi obbligatori per (tipo, stato), indipendentemente dai valori della riga.
  // conditionalOn NON si applica qui: dipende dai valori e va valutato per riga.
  const requiredFieldsForStatus = (projectTypeId, statusId) => {
    const key = `${currentEpoch()}|${projectTypeId}|${statusId}`
    const cached = requirementCache.get(key)
    if (cached) return cached

    const all = projectStore.fields
    const result = !Array.isArray(all) ? [] : all.filter(field => {
      if (!field?.name) return false
      if (field.name === 'activeStatus' || field.name === 'status') return false
      if (Array.isArray(field.visibleInProjectTypes) && projectTypeId !== null &&
          !field.visibleInProjectTypes.includes(projectTypeId)) {
        return false
      }
      return isFieldRequiredAt(field, statusId)
    })

    requirementCache.set(key, result)
    return result
  }

  // Etichetta del campo. getFieldLabel in ProjectForm usa t() nudo e stamperebbe la chiave grezza
  // per i campi assenti dai file di traduzione: qui la guardia te() evita il problema.
  const fieldLabel = (field) => {
    const key = `projects.fields.${field.name}`
    if (te(key)) return t(key)
    return field.label || field.name
  }

  /**
   * Cosa manca a questo progetto per poter avanzare di stato.
   * @returns {{terminal: boolean, nextStates: object[], missing: {name,label,type,requiresVerification}[]}}
   */
  const missingFieldsForNextStatus = (project) => {
    if (!project) return { terminal: true, nextStates: [], missing: [] }

    const epoch = currentEpoch()
    const cached = rowCache.get(project)
    if (cached && cached.epoch === epoch) return cached.info

    const nextStates = resolveNextStates(project)

    let info
    if (nextStates.length === 0) {
      info = { terminal: true, nextStates: [], missing: [] }
    } else {
      const projectTypeId = toInt(project.projectType)

      // Intersezione fra i requisiti di tutti gli stati successivi visibili: si elenca solo ciò
      // che serve comunque, qualunque ramo l'utente prenda. (Con la regola cumulativa questa
      // coincide con lo stato di destinazione di id più basso, ma non ci facciamo affidamento.)
      const lists = nextStates.map(s => requiredFieldsForStatus(projectTypeId, s.id))
      const others = lists.slice(1).map(list => new Set(list.map(f => f.name)))
      const common = lists[0].filter(f => others.every(set => set.has(f.name)))

      const missing = common
        .filter(field => isFieldVisibleFor(project, field))
        .filter(field => isFieldEmpty(project, field))
        .map(field => ({
          name: field.name,
          label: fieldLabel(field),
          type: field.type,
          requiresVerification: requiresVerification(field)
        }))

      info = { terminal: false, nextStates, missing }
    }

    rowCache.set(project, { epoch, info })
    return info
  }

  return {
    resolveNextStates,
    requiredFieldsForStatus,
    missingFieldsForNextStatus
  }
}
